
import { Interval, Duration } from 'luxon';
import React from "react";

import * as d3 from 'd3';

import './TimelineWidget.css'
import TimelinePlot from './TimelinePlot.js';
import PartitionWidget from './PartitionWidget.js';
import {SelectionInterval} from './SelectionInterval.js';
import svgToCanvas from './svgToCanvas';
import {zip} from './utils.js'

function screenToSVGCoords(canvas, e) {
    // from https://observablehq.com/@danburzo/dragging-a-svg-rectangle-on-the-screen
    // Read the SVG's bounding rectangle...
    let canvasRect = canvas.getBoundingClientRect();
    // ...and transform clientX / clientY to be relative to that rectangle
    return {
        x: e.clientX - canvasRect.x,
        y: e.clientY - canvasRect.y
    }
}

class TimelineWidget extends React.Component {
    constructor(props) {
        super(props);

        this._which_end = null;
        this._delta_x = null;
        
    }

    componentDidMount() {
        this.drawTimeline();
        this.props['setscreenshotfunc'](this.screenshot.bind(this), 'timeline');
    }

    componentDidUpdate() {
        this.drawTimeline();
    }

    wheel(new_intv) {
        if (this.props['ontboundschange'] !== undefined) {
            this.props['ontboundschange'](new_intv);
        }
        this.drawTimeline();
    }

    drawTimeline() {
        const t_scale = this.props['taxis'];

        const selection_x_start = t_scale.map(this.props['interval'].start());
        const selection_x_end = t_scale.map(this.props['interval'].end());

        var selection_box = d3.select('#timeline-select-box');

        // Remove all the stuff that was there
        selection_box.selectAll('*').remove();

        // Add light gray selection box
        selection_box.append('rect')
            .attr('x', selection_x_start)
            .attr('y', this.props['margins']['top'])
            .attr('width', selection_x_end - selection_x_start)
            .attr('height', this.props['height'] - this.props['margins']['top'] - this.props['margins']['bottom'])
            .attr('fill', '#d9dddd')
            .attr('class', 'selection')
            .attr('clip-path', 'url(#axis-face)');


        // Add dark gray left drag handle
        selection_box.append('rect')
            .attr('x', selection_x_start - 4)
            .attr('y', this.props['margins']['top'])
            .attr('width', 8)
            .attr('height', this.props['height'] - this.props['margins']['top'] - this.props['margins']['bottom'])
            .attr('fill', '#a0abab')
            .attr('class', 'handle-left')
            .attr('clip-path', 'url(#axis-face)');

        // Add dark gray right drag handle
        selection_box.append('rect')
            .attr('x', selection_x_end - 4)
            .attr('y', this.props['margins']['top'])
            .attr('width', 8)
            .attr('height', this.props['height'] - this.props['margins']['top'] - this.props['margins']['bottom'])
            .attr('fill', '#a0abab')
            .attr('class', 'handle-right')
            .attr('clip-path', 'url(#axis-face)');

        // The mouse events are handled in the topmost layer, not where stuff is drawn (because the visible handle can be
        //  hidden behind other things.)
        var mouse_layer = d3.select('#timeline-mouse-layer');

        // Remove all the stuff that was in the mouse layer
        mouse_layer.selectAll('*').remove();

        // Add the transparent click box for the left drag handle
        mouse_layer.append('rect')
            .attr('x', selection_x_start - 4)
            .attr('y', this.props['margins']['top'])
            .attr('width', 8)
            .attr('height', this.props['height'] - this.props['margins']['top'] - this.props['margins']['bottom'])
            .attr('class', 'handle-left-mouse')
            .attr('clip-path', 'url(#axis-face)');

        // Add the transparent click box for the right drag handle
        mouse_layer.append('rect')
            .attr('x', selection_x_end - 4)
            .attr('y', this.props['margins']['top'])
            .attr('width', 8)
            .attr('height', this.props['height'] - this.props['margins']['top'] - this.props['margins']['bottom'])
            .attr('class', 'handle-right-mouse')
            .attr('clip-path', 'url(#axis-face)');

        let env_markers = d3.select('#env-markers');
        env_markers.selectAll('*').remove();

        if (this.props['show_env_markers']) {
            const t_scale_mkr = this.props['taxis'].withMinScale(Duration.fromISO('P1D'));
            const env_days = t_scale_mkr.getBarSubintervals(this.props['cday_offset']);
            const env_days_data = this.props['env_handler'].availableDaysInIntervals(env_days, this.props['cday_offset']);

            const marker_data = [...zip(env_days, env_days_data)].filter(([ed, edd]) => edd > 0)
                                                                 .map(([ed, edd]) => { return {'sintv': ed, 'data': edd}; })

            env_markers.selectAll('g')
                .data(marker_data)
                .enter()
                    .append('rect')
                    .attr('class', 'env-marker env-marker-clickable')
                    .attr('x', d => t_scale.map(d['sintv'].start))
                    .attr('y', 0)
                    .attr('width', d => t_scale.map(d['sintv'].end) - t_scale.map(d['sintv'].start))
                    .attr('height', 5)
                    .attr('clip-path', 'url(#axis-face)')
                    .attr('data-interval', d => d['sintv'].toISO());
        }
    }

    handleDragStart(event) {
        var svg = document.getElementsByTagName('svg')[0];
        var coords_svg = screenToSVGCoords(svg, event);
        var target_x = d3.select(event.target).attr('x')

        if (d3.select(event.target).classed('handle-left-mouse')) {
            this._which_end = 'left';
        }
        else if (d3.select(event.target).classed('handle-right-mouse')) {
            this._which_end = 'right';
        }
        else if (d3.select(event.target).classed('env-marker')) {
            return;
        }
        
        if (this._which_end !== null) {
            this._delta_x = coords_svg['x'] - target_x;
        }
        else {
            this._delta_x = coords_svg['x'];
        }
    }

    handleDragMove(event) {
        if (!this.isDraggingHandle() && !this.isDraggingTimeline()) {
            return;
        }

        const t_scale = this.props['taxis'];
        const zoom_limit_intv = t_scale.getPaddedBounds();

        var svg = document.getElementsByTagName('svg')[0];
        var coords_svg = screenToSVGCoords(svg, event);
        var new_x = coords_svg['x'] - this._delta_x;

        var new_intv_start = null, new_intv_end = null;
        var new_tbounds_start = null, new_tbounds_end = null;

        if (this._which_end == 'left') {
            let selection = d3.select('.selection');
            const old_start = parseFloat(selection.attr('x'));
            const old_end = old_start + parseFloat(selection.attr('width'));

            new_x = Math.min(new_x, old_end - 3 * 4);

            // Figure out what the new interval is
            new_intv_start = t_scale.mapInverse(new_x + 4);
            new_intv_end = t_scale.mapInverse(old_end);
        }
        else if (this._which_end == 'right') {
            let selection = d3.select('.selection');
            const old_start = parseFloat(selection.attr('x'));

            new_x = Math.max(new_x, old_start + 4);

            // Figure out what the new interval is
            new_intv_start = t_scale.mapInverse(old_start);
            new_intv_end = t_scale.mapInverse(new_x + 4);
        }
        else {
            const orig_dt = t_scale.mapInverse(this._delta_x);
            const drag_dt = t_scale.mapInverse(coords_svg['x']);
            const drag_dur = Duration.fromMillis(orig_dt.toMillis() - drag_dt.toMillis());
            new_tbounds_start = t_scale.t_bnds_intv.start.plus(drag_dur);
            new_tbounds_end = t_scale.t_bnds_intv.end.plus(drag_dur);

            const t_bnds_dur = t_scale.t_bnds_intv.toDuration();

            if (new_tbounds_start < zoom_limit_intv.start) {
                new_tbounds_start = zoom_limit_intv.start;
                new_tbounds_end = new_tbounds_start.plus(t_bnds_dur);
            }

            if (new_tbounds_end > zoom_limit_intv.end) {
                new_tbounds_end = zoom_limit_intv.end;
                new_tbounds_start = new_tbounds_end.minus(t_bnds_dur);
            }

            this._delta_x = coords_svg['x'];
        }

        if (new_intv_start !== null && new_intv_end !== null) {
            let new_intv;
            if (new_intv_start < this.props['data_limits'].start) {
                new_intv_start = this.props['data_limits'].start;
            }
            
            if (new_intv_end > this.props['data_limits'].end) {
                new_intv_end = this.props['data_limits'].end;
            }

            if (this.props['interval'].isYearly()) {
                new_intv = this.props['interval'].setYearBounds([new_intv_start.year, new_intv_end.year]);
            }
            else {
                new_intv = new SelectionInterval(Interval.fromDateTimes(new_intv_start, new_intv_end));
            }

            if (this.props['onintervalchange'] !== undefined && !new_intv.equals(this.props['interval'])) {
                this.props['onintervalchange'](new_intv);
            }
        }
        else if (new_tbounds_start !== null && new_tbounds_end !== null) {
            if (this.props['ontboundschange'] !== undefined) {
                const new_tbounds = Interval.fromDateTimes(new_tbounds_start, new_tbounds_end);
                this.props['ontboundschange'](new_tbounds);
            }
        }
    }

    handleDragEnd(event) {
        if (d3.select(event.target).classed('env-marker')) {
            const new_intv = new SelectionInterval(Interval.fromISO(event.target.dataset.interval, {'zone': 'utc'}));

            if (this.props['onintervalchange'] !== undefined) {
                this.props['onintervalchange'](new_intv, true);
            }
            return;
        }
        this._delta_x = null;
        this._which_end = null;
    }

    isDraggingHandle() {
        return this._delta_x !== null && this._which_end !== null;
    }

    isDraggingTimeline() {
        return this._delta_x !== null && this._which_end === null;
    }

    screenshot() {
        const dpr = window.devicePixelRatio || 1;

        let canvas = document.createElement('canvas');
        canvas.width = this.props['width'] * dpr;
        canvas.height = this.props['height'] * dpr;

        svgToCanvas(this.svg, canvas, 0, 0, dpr);

        return canvas.toDataURL();
    }

    render() {
        const width = this.props['width'] - this.props['margins']['right'] - this.props['margins']['left'];
        const height = this.props['height'] - this.props['margins']['bottom'] - this.props['margins']['top'];

        return (
            <div className='timeline-container'>
                <svg id='timeline' ref={el => this.svg = el} 
                    onMouseDown={this.handleDragStart.bind(this)}
                    onMouseMove={this.handleDragMove.bind(this)}
                    onMouseUp={this.handleDragEnd.bind(this)}
                >
                    <clipPath id='axis-face'>
                        <rect 
                            x={this.props['margins']['left']}
                            y={0}
                            width={width}
                            height={height + this.props['margins']['top']}
                        ></rect>
                    </clipPath>
                    <g id='env-markers'></g>
                    <g id='timeline-select-box'></g>
                    <TimelinePlot
                        t-axis={this.props['taxis']}
                        x={this.props['margins']['left']}
                        y={this.props['margins']['top']}
                        width={width}
                        height={height}
                        onwheel={this.props['ontboundschange']}
                        tor_coll={this.props['tor_coll']}
                        interval={this.props['interval']}
                        partitioner={this.props['partitioner']}
                        part_select={this.props['part_select']}
                        cday_offset={this.props['cday_offset']}
                    />
                    <g id='timeline-mouse-layer'></g>
                    <PartitionWidget 
                        partitioner={this.props['partitioner']}
                        x={this.props['margins']['left'] + width + 5}
                        y={this.props['margins']['top']}
                        width={45}
                        height={height}
                    />
                </svg>
            </div>
        );
    }
}

export default TimelineWidget;
